/**
 * The inventory Table mode (Phase C2): dense rows, a column chooser saved per device, a sticky
 * header whose sortable columns map onto the list's existing server sorts, inline price and stock
 * edits, and a keyboard grid — ↑/↓ (Home/End, PgUp/PgDn) move the active row, Enter opens the
 * drawer, Space ticks, `e` edits the price and `q` the stock (`/` to search is the page's).
 *
 * Built on the card list's performance work (#168): virtualised with window scroll and a
 * hoisted, stable `List` component; memoised rows fed one stable actions object; the active row,
 * the edit box and each cell's save status are passed only to the row they belong to.
 *
 * Not offered on phones (the page falls back to compact cards below 768 px): twenty columns of
 * numbers do not fit a phone, and the cards already carry stock and price there.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type ComponentPropsWithoutRef, type CSSProperties, type KeyboardEvent } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrency } from '../../../contexts/CurrencyContext'
import { useStableHandlers } from './useStableHandlers'
import { useInventoryTableEdits } from './useInventoryTableEdits'
import { InventoryTableRow, rowDomId, type InventoryTableRowActions } from './InventoryTableRow'
import {
  ariaSortFor,
  columnsFor,
  nextSortFor,
  readTableColumns,
  salesViewColumnIds,
  tableGridTemplate,
  tableMinWidth,
  type InventoryTableColumnId,
} from './inventoryTableColumns'
import { moveActiveIndex, priceEditInitialValue, tableKeyAction, type EditableField } from './inventoryTableEdit'
import { inventoryCopy } from './inventoryCopy'
import type { InventorySortOption } from './inventoryListQuery'
import type { InventoryProduct } from './inventoryTypes'
import styles from './InventoryTable.module.css'

/** Fixed row height: Virtuoso skips measuring every row. */
export const TABLE_ROW_HEIGHT = 44

// Stable identity across renders (see InventoryProductsList for why).
const TableBody = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(function TableBody(props, ref) {
  return <div {...props} ref={ref} role="rowgroup" className={styles.body} />
})
const TABLE_COMPONENTS = { List: TableBody }
const OVERSCAN = { main: 400, reverse: 400 }
const productKey = (_index: number, product: InventoryProduct) => product.id

export interface InventoryTableProps {
  products: InventoryProduct[]
  locale: string
  selectedIds: Set<string>
  highlightedProductId: string | null
  canBulkOps: boolean
  /** Plan allows edits (the server still checks every save). */
  canInventoryWrite: boolean
  sortOption: InventorySortOption
  onSortChange: (sort: InventorySortOption) => void
  onToggleSelect: (productId: string) => void
  onToggleSelectPage: () => void
  onOpen: (product: InventoryProduct) => void
  onOpenImage: (url: string, alt: string) => void
  /** Sold out or Recent sales is showing, so the sale columns belong on screen. */
  salesView?: boolean
  /** The Recent sales window, when the list has one. */
  soldWindowDays?: number | null
  /** The columns chosen in the page toolbar's Columns menu; this device's saved choice when absent. */
  visibleColumnIds?: readonly InventoryTableColumnId[]
}

export function InventoryTable({
  products,
  locale,
  salesView = false,
  soldWindowDays = null,
  selectedIds,
  highlightedProductId,
  canBulkOps,
  canInventoryWrite,
  sortOption,
  onSortChange,
  onToggleSelect,
  onToggleSelectPage,
  onOpen,
  onOpenImage,
  visibleColumnIds,
}: InventoryTableProps) {
  const queryClient = useQueryClient()
  const { formatPrice, tryConvertPrice, currency, baseCurrency } = useCurrency()
  const edits = useInventoryTableEdits({ queryClient, currency, baseCurrency, tryConvertPrice, formatPrice })

  const [savedIds] = useState<InventoryTableColumnId[]>(() => readTableColumns())
  const visibleIds = visibleColumnIds ?? savedIds
  const shownIds = useMemo(() => (salesView ? salesViewColumnIds(visibleIds) : visibleIds), [salesView, visibleIds])
  const columns = useMemo(
    () => columnsFor(shownIds).map((column) =>
      column.id === 'unitsSold' && soldWindowDays !== null ? { ...column, label: inventoryCopy.sales.unitsShort(soldWindowDays) } : column),
    [shownIds, soldWindowDays],
  )
  const gridTemplate = useMemo(() => tableGridTemplate(columns), [columns])
  const minWidth = useMemo(() => tableMinWidth(columns), [columns])
  const headScrollerRef = useRef<HTMLDivElement>(null)
  const bodyScrollerRef = useRef<HTMLDivElement>(null)

  const [activeIndex, setActiveIndex] = useState(-1)
  const [editing, setEditing] = useState<{ productId: string; field: EditableField; initial: string } | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const virtuosoRef = useRef<VirtuosoHandle>(null)

  // A new page or filter: the active row no longer means anything past the end.
  useEffect(() => {
    setActiveIndex((index) => (index >= products.length ? products.length - 1 : index))
  }, [products.length])

  const focusGrid = () => gridRef.current?.focus({ preventScroll: true })

  // Keep the active row in view below the sticky header (and the app header above it): a plain
  // scrollIntoView parks it underneath them. A row that isn't rendered yet is centred first.
  const headRef = useRef<HTMLDivElement>(null)
  const activate = useCallback((index: number) => {
    setActiveIndex(index)
    const product = index >= 0 ? products[index] : undefined
    if (!product) return
    const row = document.getElementById(rowDomId(product.id))
    if (!row) {
      virtuosoRef.current?.scrollToIndex({ index, align: 'center', behavior: 'auto' })
      return
    }
    const top = headRef.current?.getBoundingClientRect().bottom ?? 0
    const rect = row.getBoundingClientRect()
    if (rect.top < top) window.scrollBy({ top: rect.top - top - 4 })
    else if (rect.bottom > window.innerHeight) window.scrollBy({ top: rect.bottom - window.innerHeight + 4 })
  }, [products])

  const startEdit = (index: number, field: EditableField) => {
    const product = products[index]
    if (!product || !canInventoryWrite) return
    setActiveIndex(index)
    edits.clear(product.id)
    setEditing({
      productId: product.id,
      field,
      initial: field === 'price' ? priceEditInitialValue(product, currency) : String(product.quantity),
    })
  }

  const rowActions = useStableHandlers<InventoryTableRowActions>({
    activate: (index) => {
      setActiveIndex(index)
      focusGrid()
    },
    open: (product) => onOpen(product),
    toggleSelect: (productId) => onToggleSelect(productId),
    startEdit: (index, field) => startEdit(index, field),
    commitEdit: (product, field, text) => {
      void edits.save(product, field, text).then((done) => {
        if (done) {
          setEditing(null)
          focusGrid()
        }
      })
    },
    cancelEdit: () => {
      setEditing(null)
      focusGrid()
    },
    openImage: (url, alt) => onOpenImage(url, alt),
  })

  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Keys typed into a control inside the grid (a tick box, the column chooser) are theirs.
    if (event.target !== event.currentTarget) return
    const action = tableKeyAction(event)
    if (!action) return
    event.preventDefault()
    const product = activeIndex >= 0 ? products[activeIndex] : undefined
    switch (action) {
      case 'open':
        if (product) onOpen(product)
        return
      case 'toggle':
        if (product && canBulkOps) onToggleSelect(product.id)
        return
      case 'editPrice':
        if (product) startEdit(activeIndex, 'price')
        return
      case 'editQuantity':
        if (product) startEdit(activeIndex, 'quantity')
        return
      default:
        activate(moveActiveIndex(activeIndex, action, products.length))
    }
  }

  const allOnPageSelected = products.length > 0 && products.every((p) => selectedIds.has(p.id))
  const activeId = activeIndex >= 0 ? products[activeIndex]?.id : undefined

  return (
    <div className={styles.tableWrap}>
      <div className={styles.toolbar}>
        <span className={styles.hint}>
          ↑↓ move · Enter open · Space tick · <kbd>e</kbd> price · <kbd>q</kbd> stock · <kbd>/</kbd> search
        </span>
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label="Inventory products"
        aria-rowcount={products.length + 1}
        aria-colcount={columns.length + 1}
        aria-multiselectable={canBulkOps || undefined}
        aria-activedescendant={activeId ? rowDomId(activeId) : undefined}
        tabIndex={0}
        className={styles.grid}
        style={{ '--table-min-width': `${minWidth}px` } as CSSProperties}
        onKeyDown={onGridKeyDown}
        onFocus={(event) => {
          if (event.target === event.currentTarget && activeIndex < 0 && products.length > 0) setActiveIndex(0)
        }}
      >
        <div role="rowgroup" className={styles.head} ref={headRef}>
          {/* The sideways scrollbar lives here, on the sticky header, so it is on screen however long
              the page is; the body follows it (and it follows the body for shift-wheel and keys). */}
          <div
            className={styles.headScroller}
            ref={headScrollerRef}
            onScroll={(event) => {
              const body = bodyScrollerRef.current
              if (body && body.scrollLeft !== event.currentTarget.scrollLeft) body.scrollLeft = event.currentTarget.scrollLeft
            }}
          >
            <div role="row" aria-rowindex={1} className={styles.headRow} style={{ gridTemplateColumns: gridTemplate }}>
              <div role="columnheader" className={styles.checkCell}>
                <input
                  type="checkbox"
                  tabIndex={-1}
                  checked={allOnPageSelected}
                  disabled={!canBulkOps || products.length === 0}
                  onChange={onToggleSelectPage}
                  aria-label="Select every product on this page"
                />
              </div>
              {columns.map((column) => {
                const sort = ariaSortFor(column, sortOption)
                return (
                  <div key={column.id} role="columnheader" aria-sort={sort} className={`${styles.headCell} ${column.numeric ? styles.cellNumeric : ''}`}>
                    {column.sort ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        className={styles.sortButton}
                        onClick={() => {
                          const next = nextSortFor(column, sortOption)
                          if (next) onSortChange(next)
                        }}
                      >
                        {column.label}
                        <span aria-hidden className={styles.sortMark}>{sort === 'ascending' ? '▲' : sort === 'descending' ? '▼' : ''}</span>
                      </button>
                    ) : column.label}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div
          className={styles.bodyScroller}
          ref={bodyScrollerRef}
          onScroll={(event) => {
            const head = headScrollerRef.current
            if (head && head.scrollLeft !== event.currentTarget.scrollLeft) head.scrollLeft = event.currentTarget.scrollLeft
          }}
        >
          <Virtuoso
            ref={virtuosoRef}
            useWindowScroll
            data={products}
            computeItemKey={productKey}
            fixedItemHeight={TABLE_ROW_HEIGHT}
            overscan={OVERSCAN}
            components={TABLE_COMPONENTS}
            itemContent={(index, product) => {
              const rowEditing = editing?.productId === product.id ? editing : null
              return (
                <InventoryTableRow
                  product={product}
                  index={index}
                  columns={columns}
                  gridTemplate={gridTemplate}
                  active={index === activeIndex}
                  selected={selectedIds.has(product.id)}
                  highlighted={product.id === highlightedProductId}
                  canBulkOps={canBulkOps}
                  canEdit={canInventoryWrite}
                  editing={rowEditing?.field ?? null}
                  editInitial={rowEditing?.initial ?? ''}
                  status={edits.status.get(product.id)}
                  locale={locale}
                  formatPrice={formatPrice}
                  soldWindowDays={soldWindowDays}
                  actions={rowActions}
                />
              )
            }}
          />
        </div>
      </div>
    </div>
  )
}
